import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { User } from 'oidc-client-ts';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'store/store';
import { isValidEmailAddress } from 'utils/inputProcessingUtils/inputProcessingUtils';
import { selectHospitalLogoUrl, setCredentials } from '../authSlice';
import { ErrorCodes } from '../constants';
import { LoginFormInputs, OidcConfig } from '../types';
import { useLogin } from './useLogin';
import { useOidcClient } from './useOidcClient';

export enum LoginFormStep {
  EMAIL = 0,
  PASSWORD = 1,
  FORGOT_PASSWORD = 2,
  PASSWORD_RESET_CONFIRMATION = 3,
}

// Hook to be used in the LoginForm component
export const useLoginForm = () => {
  const navigate = useNavigate();
  const { translation } = useTranslationContext();
  const {
    onSubmitForm,
    resetPassword,
    isLoading,
    validateEmail,
    showAccessRestrictionDialog,
    toggleAccessRestrictionDialog,
  } = useLogin();
  const hospitalLogoUrl = useSelector(selectHospitalLogoUrl);
  const [step, setStep] = useState<LoginFormStep>(LoginFormStep.EMAIL);
  const { handleOIDCCallback, handleOIDCSignIn, hasAuthCode } = useOidcClient();
  const appDispatch = useAppDispatch();

  const { setError, control, formState, getValues, handleSubmit, reset, setValue } =
    useForm<LoginFormInputs>({
      mode: 'onSubmit',
    });

  const validationRules = {
    email: {
      validate: (input: string) => isValidEmailAddress(input) || translation('email_invalid_error'),
    },
    password: {
      required: translation('password_field_empty_error'),
    },
  };

  const showBackButton = step === LoginFormStep.PASSWORD || step === LoginFormStep.FORGOT_PASSWORD;

  const email = getValues('email');

  // Below user effect is for SSO flow handling OIDC callback
  // Step 1 - handleForwardButtonClick method calls validateEmailEndpoint.
  // Step 2 - if response is empty, we assume user is not SSO user and we move to password step
  // Step 3 - if response is not empty, user is SSO user and we start SSO flow

  // SSO Login Flow steps:
  // Step 3 - continued - userManager.signinRedirect() is called to initiate the redirect to the SSO provider
  // Step 4 - user authenticates on the SSO provider and is redirected back to the app with a code and state
  // Step 5 - handleOIDCCallback is called when user returns to our app with a code
  // Step 6 - userManager.signinRedirectCallback() returns a user object which
  // contains the access token and refresh token
  // Step 7 - we set the access token and refresh token in the store using loginActionDispatch
  // and navigate to the home page

  const onSSOLoginError = (errorMessage: string) => {
    console.warn('SSO Login Error:', errorMessage);
    setError('email', {
      type: 'server',
      message: translation('generic_failure_error'),
    });
  };

  const onSSOLoginSuccess = (user: User) => {
    // Store the tokens in Redux
    appDispatch(
      setCredentials({
        accessToken: user?.access_token,
        refreshToken: user?.refresh_token || '',
      }),
    );

    // Navigate to the home page
    navigate('/home');
  };

  useEffect(() => {
    // Check if the URL contains the callback parameters
    if (hasAuthCode) {
      handleOIDCCallback()
        .then((user) => {
          onSSOLoginSuccess(user);
        })
        .catch((error) => {
          onSSOLoginError(error.message);
        });
    }
  }, [hasAuthCode, handleOIDCCallback]);

  // Click Handlers
  const onErrorCallback = (error: string) => {
    // Check if member web is disabled at hospital level (403 with APPLICATION_FORBIDDEN)
    if (error === ErrorCodes.APPLICATION_FORBIDDEN) {
      toggleAccessRestrictionDialog();
      return;
    }

    // Set form error on password field for display Inline
    setError('password', {
      type: 'server',
      message: error,
    });
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword(getValues()?.forgotPassword);
      setStep(LoginFormStep.PASSWORD_RESET_CONFIRMATION);
    } catch {
      setError('forgotPassword', {
        type: 'server',
        message: translation('generic_failure_error'),
      });
    }
  };

  const handleForwardButtonClick = async (data: LoginFormInputs) => {
    // eslint-disable-next-line default-case
    switch (step) {
      case LoginFormStep.EMAIL:
        // as user entered their email, calling validate email endpoint
        try {
          // this endpoint returns empty response for NON SSO users and openidconnect info for SSO users.
          const response = await validateEmail(data.email);
          if (response.data && Object.keys(response.data).length > 0) {
            // response is not empty, this is a SSO user

            // we call handleOIDCSignIn to initiate the SSO flow
            // this will redirect the user to the SSO provider
            // and we will handle the callback in the useEffect handleOIDCCallback above
            await handleOIDCSignIn(response.data as OidcConfig);
          } else {
            setStep(LoginFormStep.PASSWORD);
          }
          break;
        } catch {
          setError('email', {
            type: 'server',
            message: translation('generic_failure_error'),
          });
          return;
        }
      case LoginFormStep.PASSWORD:
        onSubmitForm({ email: data.email, password: data.password }, onErrorCallback);
        break;
      case LoginFormStep.FORGOT_PASSWORD:
        handleResetPassword();
        return;
      case LoginFormStep.PASSWORD_RESET_CONFIRMATION:
        setStep(LoginFormStep.EMAIL);
        reset();
        break;
    }

    // The form is submitted when the forward button is clicked.
    // You can verify this by accessing the formState isSubmitted property.
    // Therefore, to prevent immediate validation erros when editing subsequent inputs,
    // we reset the form but keep all previously entered values.
    reset(undefined, { keepValues: true });
  };

  const handleBackButtonClick = () => {
    if (step > 0) {
      setStep((prevStep) => prevStep - 1);
    }
  };

  const handleForgotPasswordButtonClick = () => {
    setStep(LoginFormStep.FORGOT_PASSWORD);
    setValue('forgotPassword', getValues('email'));
    reset(undefined, { keepValues: true });
  };

  const forwardButtonLabel = useMemo(() => {
    switch (step) {
      case LoginFormStep.EMAIL:
        return translation('continue_button_title');
      case LoginFormStep.PASSWORD:
        return translation('login');
      case LoginFormStep.FORGOT_PASSWORD:
        return translation('forgot_password_reset_button_text');
      case LoginFormStep.PASSWORD_RESET_CONFIRMATION:
        return translation('back_to_log_in');
      default:
        return '';
    }
  }, [translation, step]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case LoginFormStep.FORGOT_PASSWORD:
        return translation('forgot_password');
      case LoginFormStep.PASSWORD_RESET_CONFIRMATION:
        return translation('reset_password_alert');
      default:
        // no title for other steps (logo is shown instead)
        return undefined;
    }
  }, [translation, step]);

  return {
    control,
    email,
    handleBackButtonClick,
    handleForgotPasswordButtonClick,
    handleForwardButtonClick: handleSubmit(handleForwardButtonClick),
    isForwardButtonDisabled: !formState.isDirty,
    showBackButton,
    step,
    validationRules,
    isLoading,
    forwardButtonLabel,
    stepTitle,
    showAccessRestrictionDialog,
    toggleAccessRestrictionDialog,
    hospitalLogoUrl,
  };
};
